<script lang="ts">
	import { createEventDispatcher, onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import { ButtonV2, ListItem, Modal, Text } from '@Shelly/shelly-design-system/index';
	import type { CloudRecording } from '../../types/cloud-recordings';
	import { createThumbnailResolver } from '../../helpers/encryption/recording-image-decryption';
	import { getDetailedObjectRows, getDetectedObjectBoxes } from '../../helpers/camera-objects';
	import ZoomableContainer from '../../CameraPlayer/ZoomableContainer.svelte';
	import DetectionConfidenceModal from '../CameraSettings/CameraStorage/DetectionConfidenceModal.svelte';

	export let showModal = false;
	export let recording: CloudRecording;

	const dispatch = createEventDispatcher();

	let imageUrl = '';
	let naturalWidth = 0;
	let naturalHeight = 0;
	let hoveredId: string | null = null;
	let pinnedId: string | null = null;
	let showConfidenceModal = false;

	// Custom <img on:load> is kept for naturalWidth/Height (bounding boxes); the
	// resolver supplies the (memoized, decrypted) URL and resets dimensions on swap.
	const imageResolver = createThumbnailResolver((url) => {
		imageUrl = url;
		naturalWidth = 0;
		naturalHeight = 0;
	});

	// Only resolve (fetch + decrypt the full-size image) while the modal is open —
	// one instance is mounted per recording row, so resolving eagerly would decrypt
	// every large thumbnail up-front. Pass `recDate` so the correct rotation era's
	// key is used (omitting it would resolve the newest era and fail to decrypt
	// older-era recordings).
	$: imageResolver.update({
		deviceId: recording?.mac ?? '',
		url: showModal ? recording?.imageUrls?.large : undefined,
		size: 'large',
		encrypted: !!recording?.encrypted,
		recId: recording?.id,
		recDate: recording?.date,
	});

	onDestroy(imageResolver.destroy);

	$: objects = recording?.classification?.objects ?? [];
	$: elapsedMs = recording?.classification?.elapsedMs;
	$: detectedObjects = getDetailedObjectRows(objects);
	$: boxLayouts = getDetectedObjectBoxes(objects, naturalWidth, naturalHeight);
	$: highlightedId = hoveredId ?? pinnedId;
	$: modalTitle = $format('camera_recording_ai_detection', { default: 'AI Detection' });

	const onImageLoad = (e: Event) => {
		const img = e.currentTarget as HTMLImageElement;
		naturalWidth = img.naturalWidth;
		naturalHeight = img.naturalHeight;
	};

	/*
	 * Touch never sends a leave event, so a hover left over from a tap would
	 * shadow the pin and keep a box lit after it was unpinned. A real mouse sets
	 * it again on the next move.
	 */
	const togglePinned = (id: string) => {
		hoveredId = null;
		pinnedId = pinnedId === id ? null : id;
	};

	/*
	 * iOS fakes a hover on tap and suppresses the click when that changes what is
	 * on screen, so only a mouse is allowed to highlight.
	 */
	const handleRowPointerEnter = (event: PointerEvent, id: string) => {
		if (event.pointerType !== 'mouse') return;

		hoveredId = id;
	};

	const handleRowPointerLeave = (event: PointerEvent) => {
		if (event.pointerType !== 'mouse') return;

		hoveredId = null;
	};

	const handleRowKeydown = (e: KeyboardEvent, id: string) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			togglePinned(id);
		}
	};

	const handleClose = () => {
		hoveredId = null;
		pinnedId = null;
		dispatch('close');
	};

	// Closing first keeps a single dialog on screen; this component stays mounted,
	// so the confidence modal it owns survives the close.
	const openConfidenceSettings = () => {
		handleClose();
		showConfidenceModal = true;
	};
</script>

<Modal {showModal} title={modalTitle} backdropClose={true} on:close={handleClose}>
	<svelte:fragment slot="content">
		{#if imageUrl}
			<div class="ai-detection-zoom width-full">
				<ZoomableContainer>
					<div class="ai-image-wrap" class:has-active={highlightedId !== null}>
						{#key imageUrl}
							<img class="ai-image" src={imageUrl} alt="" on:load={onImageLoad} />
						{/key}
						{#each boxLayouts as layout (layout.id)}
							<div
								class="ai-box"
								class:ai-box--active={highlightedId === layout.id}
								style:--ai-box-color={layout.color}
								style:left="{layout.leftPct}%"
								style:top="{layout.topPct}%"
								style:width="{layout.widthPct}%"
								style:height="{layout.heightPct}%"
							>
								<span class="ai-box-label">{layout.label}</span>
							</div>
						{/each}
					</div>
				</ZoomableContainer>
			</div>
		{:else}
			<Text class="mb-s">
				{$format('camera_recording_no_preview', {
					default: 'No preview image available.',
				})}
			</Text>
		{/if}

		<div class="ai-detection-section">
			<Text styleType="h2" class="mb-s">
				{$format('camera_recording_detected_objects', {
					default: 'Detected objects',
				})}
				{#if detectedObjects.length > 0}
					<span class="text-color-muted">({detectedObjects.length})</span>
				{/if}
			</Text>

			{#if detectedObjects.length === 0}
				<Text styleType="body-small" class="text-color-muted">
					{$format('camera_recording_no_objects', {
						default: 'No objects detected.',
					})}
				</Text>
			{:else}
				{#each detectedObjects as obj (obj.id)}
					<div
						class="ai-detection-row cursor-pointer mb-s"
						class:ai-detection-row--selected={pinnedId === obj.id}
						role="button"
						tabindex="0"
						aria-pressed={pinnedId === obj.id}
						on:pointerenter={(e) => handleRowPointerEnter(e, obj.id)}
						on:pointerleave={handleRowPointerLeave}
						on:focus={() => (hoveredId = obj.id)}
						on:blur={() => (hoveredId = null)}
						on:click={() => togglePinned(obj.id)}
						on:keydown={(e) => handleRowKeydown(e, obj.id)}
					>
						<ListItem
							viewType="bar"
							icon={obj.icon}
							title={obj.name}
							progress={{
								percent: obj.percent,
								title: `${obj.percent}%`,
								color: obj.barColor,
								showPercentBar: true,
							}}
							a11yProps={{ progressbarAriaLabel: obj.name }}
						/>
					</div>
				{/each}
			{/if}
		</div>

		{#if typeof elapsedMs === 'number'}
			<Text styleType="body-small" class="text-color-muted mt-s">
				{$format('camera_recording_classification_time', {
					default: 'Classification time: {ms} ms',
					values: { ms: String(elapsedMs) },
				})}
			</Text>
		{/if}

		<!-- A recording with no `classification` has not been looked at yet, which
		the confidence setting does not explain; only a classified one is offered it. -->
		{#if recording?.classification}
			<div class="d-flex align-items-center gap-s mt-s">
				<Text styleType="body-extra-small" class="text-color-muted">
					{detectedObjects.length === 0
						? $format('camera_cloud_confidence_modal_hint_empty', {
								default:
									'No object here reached the confidence required to be marked. Lower the confidence to mark more next time.',
							})
						: $format('camera_cloud_confidence_modal_hint', {
								default:
									'Only objects detected with enough confidence are marked on a recording.',
							})}
				</Text>
				<ButtonV2
					size="s"
					variant="secondary"
					on:click={openConfidenceSettings}
					aria-label={$format('camera_cloud_confidence_title', {
						default: 'AI Detection confidence',
					})}
				>
					{$format('camera_cloud_confidence_manage', { default: 'Adjust' })}
				</ButtonV2>
			</div>
		{/if}
	</svelte:fragment>
</Modal>

<DetectionConfidenceModal
	deviceId={recording?.mac}
	showModal={showConfidenceModal}
	on:close={() => (showConfidenceModal = false)}
/>

<style>
	.ai-detection-zoom :global(.zoom-container) {
		border-radius: var(--radius-big);
	}

	.ai-image-wrap {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		overflow: hidden;
		background: var(--color-base);
	}

	.ai-image {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.ai-box {
		position: absolute;
		box-sizing: border-box;
		border: 2px solid var(--ai-box-color, var(--color-white));
		pointer-events: none;
		transition:
			opacity 120ms ease,
			border-width 120ms ease,
			box-shadow 120ms ease;
	}

	.ai-box-label {
		position: absolute;
		top: 0;
		left: 0;
		transform: translateY(-100%);
		margin-top: -2px;
		padding: 2px 6px;
		font-size: 11px;
		line-height: 1.2;
		white-space: nowrap;
		background: var(--ai-box-color, var(--color-white));
		color: var(--color-base);
		border-radius: var(--radius-small);
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		transition: opacity 120ms ease;
	}

	.ai-image-wrap.has-active .ai-box:not(.ai-box--active) {
		opacity: 0.35;
	}

	.ai-box--active {
		border-width: 3px;
		box-shadow: var(--shadow-glow-strong);
		z-index: 1;
	}

	.ai-detection-section {
		margin-top: var(--spacing-m);
	}
	.ai-detection-row {
		position: relative;
		border-radius: var(--radius-extra-big);
	}

	.ai-detection-row:focus-visible {
		outline: 2px solid var(--color-primary);
	}

	.ai-detection-row--selected {
		box-shadow: var(--shadow-glow-soft);
	}

	.ai-detection-row--selected::before {
		content: '';
		position: absolute;
		inset: 0;
		border: 2px solid var(--color-primary);
		border-radius: var(--radius-extra-big);
		pointer-events: none;
		z-index: 2;
	}
</style>
